/**
 * CustomDashboardPage — multi-dashboard "Build your own".
 *
 * The CEO can create as many named dashboards as they want, pin live cards
 * to each, choose how each card renders (number / bar / line / pie /
 * leaderboard / table / auto), and export the whole dashboard to CSV or PDF.
 *
 * Storage: localStorage key `fc_custom_dashboards_v2`.
 *   {
 *     dashboards: [{ id, name, cards: [{ id, prompt, response, chartType, addedAt }], createdAt, updatedAt }],
 *     activeId
 *   }
 *
 * Old `fc_custom_cards_v1` (single flat list) is migrated on first load
 * into a default dashboard called "My dashboard".
 *
 * Backend: POST /api/v1/ai/ask (same 4-layer guards as the AI chat).
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  ResponsiveContainer,
  LineChart, Line,
  BarChart, Bar,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { tokensV2 as T } from "./tokens.v2";
import { authApi } from "../../api";
import { EmptyError } from "./PageState.v2";

const STORAGE_KEY = "fc_custom_dashboards_v2";
const LEGACY_KEY  = "fc_custom_cards_v1";

const SUGGESTIONS = [
  // Revenue
  "Total revenue this month vs last month",
  "Revenue trend for the last 6 months",
  "Total brand revenue this year",
  "Revenue today by hour",
  "Average daily revenue this month",
  // Users / fans
  "How many new fans signed up today?",
  "How many new fans signed up this week?",
  "Total fans on the platform",
  "Active fans this month",
  "Top 10 paying fans this year",
  // Creators
  "Top 5 creators by earnings this month",
  "How many creators are active this month?",
  "Creators who joined in the last 30 days",
  "Total creator payouts this month",
  "How many creators got verified this quarter?",
  // Brands
  "Total brand wallet balance",
  "How many brand campaigns are active?",
  "Top 5 brands by spend this year",
  // Calls
  "How many calls were completed yesterday?",
  "Average call duration this week",
  "Call completion rate this month",
  "Total minutes talked this month",
  // Money / refunds / payouts
  "Pending payouts breakdown by status",
  "Refund volume this week vs last week",
  "Total GST collected this quarter",
  "Total TDS withheld this year",
  // Acquisition / referral
  "Referral signups this month",
  "Top 5 creators by fans referred",
];

const CHART_PALETTE = [
  T.brand.base, T.chart.violet, T.chart.mint,
  T.chart.pink, T.chart.teal, T.chart.yellow,
  T.brand.deep,
];

/* ───────────────────────── Storage ───────────────────────── */

function emptyState() {
  const id = newDashboardId();
  return {
    dashboards: [{ id, name: "My dashboard", cards: [], createdAt: Date.now(), updatedAt: Date.now() }],
    activeId: id,
  };
}

function newDashboardId() {
  return `d_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}
function newCardId() {
  return `c_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && Array.isArray(parsed.dashboards) && parsed.dashboards.length > 0 && parsed.activeId) {
        return parsed;
      }
    }
    // Migrate legacy single-list cards
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy) {
      const cards = JSON.parse(legacy);
      if (Array.isArray(cards) && cards.length > 0) {
        const id = newDashboardId();
        const migrated = {
          dashboards: [{
            id, name: "My dashboard",
            cards: cards.map(c => ({ ...c, chartType: c.chartType || "auto" })),
            createdAt: Date.now(), updatedAt: Date.now(),
          }],
          activeId: id,
        };
        // Don't delete legacy — keep as backup; user can manually clear later
        return migrated;
      }
    }
  } catch (_) { /* fall through */ }
  return emptyState();
}
function saveState(state) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); }
  catch (_) { /* private browsing */ }
}

/* ───────────────────────── Formatters ───────────────────────── */

function formatINR(n) {
  if (n == null || n === "") return "—";
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  if (num >= 1e7) return "Rs " + (num / 1e7).toFixed(2) + "Cr";
  if (num >= 1e5) return "Rs " + (num / 1e5).toFixed(2) + "L";
  if (num >= 1e3) return "Rs " + (num / 1e3).toFixed(1) + "K";
  return "Rs " + Math.round(num).toLocaleString("en-IN");
}
function formatNum(n) {
  if (n == null || n === "") return "—";
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  return num.toLocaleString("en-IN");
}
function looksLikeMoney(text) {
  return /revenue|gmv|payout|earn|spend|wallet|money|rs\b|inr|amount|paid|refund|gst|tds/i.test(String(text || ""));
}
function looksLikeTimePeriod(value) {
  if (typeof value !== "string") return false;
  return /^\d{4}-\d{2}/.test(value) || /^\d{2}\/\d{2}/.test(value) ||
         /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i.test(value);
}

/* ───────────────────────── Render shape detection ───────────────────────── */

function autoShape(response) {
  const rows = response?.results;
  if (!Array.isArray(rows) || rows.length === 0) return "answer";
  const first = rows[0];
  if (!first || typeof first !== "object") return "answer";
  const cols = Object.keys(first);
  if (cols.length === 0) return "answer";
  if (rows.length === 1 && cols.length === 1) return "number";
  if (rows.length === 1) return "table";
  // Find best-fit columns regardless of position.
  const timeCol  = findFirstColOf(rows, (v) => looksLikeTimePeriod(v));
  const labelCol = findFirstColOf(rows, (v) => typeof v === "string" && !looksLikeTimePeriod(v));
  const numericCol = findFirstColOf(rows, (_, c) => rows.every(r => isNumericValue(r[c])));

  // Fall back to Table if NO string/time column has distinguishing values
  // across rows (e.g. AI returned [{month:"Month", rev:X}, {month:"Month", rev:Y}]).
  // A leaderboard or line chart with identical labels is unreadable; the
  // user is better served by seeing the full row in a table where every
  // column is visible.
  const hasDistinguishingLabel = (timeCol && distinctCount(rows, timeCol) > 1) ||
                                 (labelCol && distinctCount(rows, labelCol) > 1);

  if (timeCol && numericCol && distinctCount(rows, timeCol) > 1) return "line";
  if (labelCol && numericCol && hasDistinguishingLabel) return "leaderboard";
  return "table";
}

const CHART_LABEL = {
  auto: "Auto", number: "Number", line: "Line", bar: "Bar",
  pie: "Pie", leaderboard: "List", table: "Table", answer: "Text",
};

/* Helpers used by availableChartTypes — detect column "kinds" */
function isNumericValue(v) {
  if (v == null) return false;
  // Postgres NUMERIC comes back as string; treat both strings and numbers.
  const n = typeof v === "number" ? v : Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n);
}
function findFirstColOf(rows, predicate) {
  if (!rows || rows.length === 0) return null;
  const cols = Object.keys(rows[0] || {});
  for (const c of cols) {
    if (predicate(rows[0][c], c, rows)) return c;
  }
  return null;
}
/* Convert any value (string, number, etc) to a finite number; null otherwise. */
function toFiniteNumber(v) {
  if (v == null) return null;
  const n = typeof v === "number" ? v : Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}
/* Truncate long label text with ellipsis for chart axes/legends. */
function clampLabel(s, max = 18) {
  const str = String(s ?? "");
  return str.length > max ? str.slice(0, max - 1) + "…" : str;
}
/* Friendly "this chart isn't right for the data" message. */
function NotChartable({ reason, suggestion }) {
  return (
    <div style={{
      padding: "32px 22px", textAlign: "center", color: T.text.secondary,
    }}>
      <div style={{
        width: 44, height: 44, borderRadius: 12,
        background: T.brand.soft, color: T.brand.base,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        fontSize: 22, fontWeight: 800, marginBottom: 12,
      }}>!</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: T.text.primary, marginBottom: 4 }}>
        Can't render this chart
      </div>
      <div style={{ fontSize: 12, color: T.text.muted, maxWidth: 280, margin: "0 auto", lineHeight: 1.5 }}>
        {reason}{suggestion ? <> · {suggestion}</> : null}
      </div>
    </div>
  );
}

/**
 * availableChartTypes — return every chart type that could meaningfully
 * render this response. Generous by design: if there's any text column +
 * any numeric column, List / Bar / Pie are all on offer (each card lets
 * the CEO experiment).
 *
 *   • no rows / no cols         → Auto + Text
 *   • 1 row × 1 col             → Auto + Number + Text
 *   • 1 row × N cols            → Auto + Number + Table + Text
 *   • N rows, has time + numeric → Auto + Line + Bar + Table + Text
 *   • N rows, has label + numeric → Auto + List + Bar + (Pie if ≤8 + positive) + Table + Text
 *   • N rows, only numeric cols  → Auto + Bar + Table + Text
 *   • N rows, only text cols     → Auto + List + Table + Text
 */
function availableChartTypes(response) {
  const rows = response?.results;
  if (!Array.isArray(rows) || rows.length === 0) return ["auto", "answer"];
  const cols = Object.keys(rows[0] || {});
  if (cols.length === 0) return ["auto", "answer"];

  // Single row → Number is meaningful
  if (rows.length === 1) {
    if (cols.length === 1) return ["auto", "number", "answer"];
    return ["auto", "number", "table", "answer"];
  }

  // Multi-row — find a usable label column and a usable numeric column.
  const timeCol = findFirstColOf(rows, (v) => looksLikeTimePeriod(v));
  const labelCol = findFirstColOf(rows, (v) => typeof v === "string" && !looksLikeTimePeriod(v));
  const numericCol = findFirstColOf(rows, (_, c) => rows.every(r => isNumericValue(r[c])));

  const types = ["auto"];
  // Time-series → Line + Bar
  if (timeCol && numericCol) {
    types.push("line", "bar");
  }
  // Label + numeric → List + Bar + Pie (Pie only when reasonable)
  if (labelCol && numericCol) {
    if (!types.includes("bar")) types.push("bar");
    types.push("leaderboard");
    const allPositive = rows.every(r => {
      const n = Number(String(r[numericCol]).replace(/,/g, ""));
      return Number.isFinite(n) && n > 0;
    });
    if (rows.length >= 2 && rows.length <= 8 && allPositive) types.push("pie");
  }
  // No label but numeric → Bar still works (use first column as x-axis)
  if (!labelCol && !timeCol && numericCol && !types.includes("bar")) {
    types.push("bar");
  }
  // Pure text data → List
  if (labelCol && !numericCol && !types.includes("leaderboard")) {
    types.push("leaderboard");
  }

  types.push("table", "answer");
  // De-dup while preserving order.
  return [...new Set(types)];
}

/* ───────────────────────── Card body renderers ───────────────────────── */

function NumberView({ response, prompt }) {
  const rows = response?.results || [];
  if (rows.length === 0) {
    return <NotChartable reason="No rows returned." suggestion="Switch to Text view." />;
  }
  const first = rows[0] || {};
  const cols = Object.keys(first);
  // Pick the first numeric column rather than blindly using cols[0] —
  // for "Top 5 with rank+name+value" results this picks `value`, not `rank`.
  const numericCol = findFirstColOf(rows, (_, c) => isNumericValue(first[c])) || cols[0];
  const value = first[numericCol];
  const numeric = toFiniteNumber(value);
  if (numeric === null) {
    return <NotChartable reason="The result has no clear numeric value." suggestion="Try Table or Text view." />;
  }
  const fmt = looksLikeMoney(prompt + " " + numericCol) ? formatINR : formatNum;
  const formatted = fmt(numeric);
  // Auto-shrink the big number on narrow cards. Each character at fontSize 38
  // is ~22px wide, so "Rs 12.34Cr" (10 chars) ≈ 220px. With 22px L/R padding
  // we need ≥264px just for the number — narrower cards (3+ in a row) clip it.
  // Step the font down based on length so it always fits without clipping.
  const big = formatted.length <= 8 ? 38 : formatted.length <= 11 ? 32 : 26;
  // Pretty-display the column-name header — ALL_CAPS_SNAKE → "All caps snake".
  const niceColName = String(numericCol)
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .replace(/^./, c => c.toUpperCase())
    .replace(/\b(\w)/g, (_, c, i) => i === 0 ? c.toUpperCase() : c.toLowerCase());
  return (
    <div style={{ padding: "24px 22px", overflow: "hidden", minWidth: 0 }}>
      <div style={{
        fontSize: 11, fontWeight: 700, letterSpacing: "0.06em",
        textTransform: "uppercase", color: T.text.muted,
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
      }} title={numericCol}>
        {niceColName}
      </div>
      <div style={{
        fontSize: big, fontWeight: 800,
        color: numeric < 0 ? T.status.bad : T.text.primary,
        letterSpacing: "-0.025em", marginTop: 6,
        fontVariantNumeric: "tabular-nums", lineHeight: 1.05,
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
      }} title={formatted}>
        {formatted}
      </div>
      {response.answer && (
        <div style={{ fontSize: 12, color: T.text.muted, marginTop: 10, lineHeight: 1.5, maxHeight: 60, overflow: "hidden" }}>
          {String(response.answer).split("\n")[0]}
        </div>
      )}
    </div>
  );
}

/* Count distinct non-null values in a column across all rows. */
function distinctCount(rows, col) {
  const seen = new Set();
  for (const r of rows) {
    const v = r?.[col];
    if (v != null && v !== "") seen.add(String(v));
  }
  return seen.size;
}

/**
 * Pick the best label + numeric columns for charting.
 *
 * The earlier version picked the first time-like column then the first
 * string column — which broke when the AI returned data like
 *   [{ month: "Month", revenue: 1.12Cr },
 *    { month: "Month", revenue: 30.9K }]
 * because both rows had the identical label "Month" and the chart was
 * unreadable. Now we prefer the column whose values are MOST distinct
 * across rows (after time-priority), so a column with unique values like
 * "May 2026" / "April 2026" beats one where every value is "Month".
 *
 * Tie-break order:
 *   1. Time-like column with > 1 distinct value
 *   2. String column with the most distinct values (must be > 1 if multi-row)
 *   3. Any string column (fallback for single-row data)
 *   4. First column (last-ditch)
 */
function pickAxes(rows) {
  if (!rows || rows.length === 0) return { xKey: null, yKey: null };
  const cols = Object.keys(rows[0]);
  const time = findFirstColOf(rows, (v) => looksLikeTimePeriod(v));
  // If the time-like column has duplicates, it isn't useful as an axis —
  // skip it and fall back to the string-distinctness search.
  const timeUseful = time && distinctCount(rows, time) > 1;

  // Find the string column with the most unique values. Beats a column
  // where every row has the same literal value (which would make every
  // bar/slice/row indistinguishable on the chart).
  let bestLabel = null;
  let bestDistinct = 0;
  for (const c of cols) {
    if (typeof rows[0][c] !== "string") continue;
    if (looksLikeTimePeriod(rows[0][c])) continue;
    const d = distinctCount(rows, c);
    if (d > bestDistinct) { bestDistinct = d; bestLabel = c; }
  }
  // Need at least 2 distinct values to be meaningful when there's >1 row.
  const labelUseful = bestLabel && (rows.length === 1 || bestDistinct > 1);

  const numeric = findFirstColOf(rows, (_, c) => rows.every(r => isNumericValue(r[c])));

  return {
    xKey: (timeUseful ? time : null) || (labelUseful ? bestLabel : null) || bestLabel || cols[0],
    yKey: numeric || cols[1] || cols[0],
  };
}

function LineView({ response, prompt }) {
  const rows = response?.results || [];
  if (rows.length < 2) return <NotChartable reason="A line chart needs at least 2 points." suggestion="Try Number or Bar." />;
  const { xKey, yKey } = pickAxes(rows);
  if (!xKey || !yKey) return <NotChartable reason="Couldn't find an x/y pair in the result." />;
  const data = rows
    .map(r => ({ x: clampLabel(r[xKey] ?? ""), y: toFiniteNumber(r[yKey]) }))
    .filter(d => d.x !== "" && d.y !== null);
  if (data.length < 2) return <NotChartable reason="Not enough valid data points after dropping nulls." />;
  const isMoney = looksLikeMoney(prompt + " " + yKey);
  data.forEach(d => { if (d.y === null) d.y = 0; });
  return (
    <div style={{ padding: "16px 12px 12px", height: 240 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={T.border.subtle} vertical={false} />
          <XAxis dataKey="x" tick={{ fontSize: 10, fill: T.text.muted }} stroke={T.border.default} />
          <YAxis tick={{ fontSize: 10, fill: T.text.muted }} stroke={T.border.default}
            tickFormatter={(v) => isMoney ? formatINR(v) : formatNum(v)} />
          <Tooltip
            formatter={(v) => isMoney ? formatINR(v) : formatNum(v)}
            contentStyle={{ background: T.surface.card, border: `1px solid ${T.border.subtle}`, borderRadius: 10, fontSize: 12 }}
            labelStyle={{ color: T.text.muted, fontWeight: 700, fontSize: 11 }}
          />
          <Line type="monotone" dataKey="y" stroke={T.brand.base} strokeWidth={2.5} dot={{ r: 3, fill: T.brand.base }} activeDot={{ r: 5 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * CssHorizontalBars — pure CSS replacement for Recharts horizontal BarChart.
 *
 * Each row is a flex column:
 *   1. Name (full width, ellipsis on overflow, hover-title shows full text)
 *   2. A relative-width fill bar (% of max value)
 *   3. The formatted value, right-aligned
 *
 * Why not Recharts? The BarChart YAxis reserved a fixed pixel width for
 * labels (160px) which was wider than the chart space available on narrow
 * cards (e.g. when 3+ cards stacked side-by-side). That caused Recharts
 * to clip Y-axis labels from the LEFT (right-aligned text) and the user
 * saw fragments like "ti Sejpal 💜" instead of "Bhakti Sejpal 💜". A pure
 * flex layout adapts to any card width with no fixed pixel budgets.
 */
function CssHorizontalBars({ data, isMoney }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map(d => Math.abs(d.y)), 1);
  return (
    <div style={{ padding: "12px 14px 14px" }}>
      {data.map((d, i) => {
        const pct = Math.max(2, (Math.abs(d.y) / max) * 100); // min 2% so 0 still shows
        const formattedValue = isMoney ? formatINR(d.y) : formatNum(d.y);
        return (
          <div key={i} style={{ marginBottom: i < data.length - 1 ? 10 : 0 }}>
            {/* Top line: name + value, both visible regardless of width */}
            <div style={{
              display: "flex", alignItems: "baseline", justifyContent: "space-between",
              gap: 10, marginBottom: 4,
            }}>
              <span title={d.label}
                style={{
                  fontSize: 12, fontWeight: 600, color: T.text.primary,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  flex: 1, minWidth: 0,
                }}>
                {d.label}
              </span>
              <span style={{
                fontSize: 12, fontWeight: 700, color: T.text.primary,
                fontVariantNumeric: "tabular-nums", flexShrink: 0,
              }}>
                {formattedValue}
              </span>
            </div>
            {/* Fill bar — gradient from brand to brand-deep */}
            <div style={{
              height: 8, borderRadius: 999,
              background: T.surface.canvas,
              overflow: "hidden",
            }}>
              <div style={{
                height: "100%", width: `${pct}%`,
                background: `linear-gradient(90deg, ${T.brand.base}, ${T.brand.deep})`,
                borderRadius: 999,
                transition: "width 280ms ease",
              }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function BarView({ response, prompt }) {
  const allRows = response?.results || [];
  if (allRows.length === 0) return <NotChartable reason="No rows returned." />;
  const { xKey, yKey } = pickAxes(allRows);
  if (!xKey || !yKey) return <NotChartable reason="Couldn't find a label + value pair." suggestion="Try Table." />;
  // If every row's label is identical (e.g. AI returned a column literally
  // named "Month" with value "Month" for both rows), suffix with row index
  // so the chart stays decipherable instead of stacking same-named bars.
  const rawLabels = allRows.slice(0, 30).map(r => String(r[xKey] ?? ""));
  const distinct = new Set(rawLabels.filter(s => s !== "")).size;
  const needsIndex = distinct < rawLabels.filter(s => s !== "").length && rawLabels.filter(s => s !== "").length > 1;
  // Horizontal layout has limited Y-axis width (160px) so we clamp shorter
  // (16 chars) here to avoid Recharts left-clipping right-aligned labels.
  const data = allRows
    .slice(0, 30)
    .map((r, i) => {
      const raw = String(r[xKey] ?? "");
      const labelText = raw === "" ? "" : (needsIndex ? `${raw} #${i + 1}` : raw);
      return {
        label: labelText,
        labelShort: clampLabel(labelText, 16),
        y: toFiniteNumber(r[yKey]) ?? 0,
      };
    })
    .filter(d => d.label !== "");
  if (data.length === 0) return <NotChartable reason="All categories were empty/null." />;
  const isMoney = looksLikeMoney(prompt + " " + yKey);
  // Time-series → vertical bars (X = time, Y = value).
  // Categorical (creator/brand/fan names, statuses, regions) → horizontal bars
  // so each label gets its own readable row instead of being squished on the X-axis.
  const isTimeSeries = data.some(d => looksLikeTimePeriod(d.label));
  const useHorizontal = !isTimeSeries;
  if (useHorizontal) {
    // Pure CSS horizontal-bar list — Recharts BarChart was clipping long
    // Y-axis names on narrow cards because the chart's reserved label area
    // (160px) was wider than the actual visible space inside the card.
    // Switching to a flex layout where each row is [name + ellipsis] over
    // [colored fill] + [value] eliminates the clipping and adapts to any
    // card width.
    return <CssHorizontalBars data={data} isMoney={isMoney} />;
  }
  return (
    <div style={{ padding: "16px 12px 12px", height: 260 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 28 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={T.border.subtle} vertical={false} />
          <XAxis dataKey="labelShort" tick={{ fontSize: 10, fill: T.text.muted }} stroke={T.border.default}
            interval={0} angle={-30} textAnchor="end" height={50} />
          <YAxis tick={{ fontSize: 10, fill: T.text.muted }} stroke={T.border.default}
            tickFormatter={(v) => isMoney ? formatINR(v) : formatNum(v)} />
          <Tooltip
            formatter={(v) => isMoney ? formatINR(v) : formatNum(v)}
            labelFormatter={(_, payload) => payload?.[0]?.payload?.label || ""}
            contentStyle={{ background: T.surface.card, border: `1px solid ${T.border.subtle}`, borderRadius: 10, fontSize: 12 }}
            cursor={{ fill: T.brand.soft }}
          />
          <Bar dataKey="y" fill={T.brand.base} radius={[6, 6, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function PieView({ response, prompt }) {
  const allRows = response?.results || [];
  if (allRows.length === 0) return <NotChartable reason="No rows returned." />;
  const { xKey: labelKey, yKey: valueKey } = pickAxes(allRows);
  if (!labelKey || !valueKey) return <NotChartable reason="Couldn't find a label + value pair for slices." />;
  // Filter NULL/empty labels and zero/negative values — pies of those are useless.
  // If labels are duplicated, suffix with #N so each slice is identifiable.
  const rawNames = allRows.slice(0, 8).map(r => String(r[labelKey] ?? "").trim());
  const distinctNames = new Set(rawNames.filter(s => s !== "")).size;
  const needsIdx = distinctNames < rawNames.filter(s => s !== "").length && rawNames.filter(s => s !== "").length > 1;
  const data = allRows
    .slice(0, 8)
    .map((r, i) => {
      const raw = String(r[labelKey] ?? "").trim();
      return {
        name: raw === "" ? "" : (needsIdx ? `${raw} #${i + 1}` : raw),
        value: toFiniteNumber(r[valueKey]) ?? 0,
      };
    })
    .filter(d => d.name !== "" && d.value > 0);
  if (data.length < 2) return <NotChartable reason="Need at least 2 positive slices." suggestion="Try Bar or List." />;
  const isMoney = looksLikeMoney(prompt + " " + valueKey);
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <div style={{ padding: "10px 12px", height: 240, display: "flex" }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={2}>
              {data.map((_, i) => <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} stroke="none" />)}
            </Pie>
            <Tooltip formatter={(v) => isMoney ? formatINR(v) : formatNum(v)}
              contentStyle={{ background: T.surface.card, border: `1px solid ${T.border.subtle}`, borderRadius: 10, fontSize: 12 }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div style={{ flex: 1, minWidth: 0, padding: "8px 0", overflowY: "auto", maxHeight: 220 }}>
        {data.map((d, i) => {
          const pct = total > 0 ? (d.value / total) * 100 : 0;
          return (
            <div key={i} title={`${d.name} · ${isMoney ? formatINR(d.value) : formatNum(d.value)}`}
              style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, padding: "5px 0" }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: CHART_PALETTE[i % CHART_PALETTE.length], flexShrink: 0 }} />
              <span style={{ flex: 1, color: T.text.primary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{clampLabel(d.name, 22)}</span>
              <span style={{ color: T.text.muted, fontVariantNumeric: "tabular-nums" }}>{pct.toFixed(1)}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LeaderboardView({ response, prompt }) {
  const rows = (response?.results || []).slice(0, 10);
  if (rows.length === 0) return <AnswerView response={response} />;
  const { xKey: labelCol, yKey: valueCol } = pickAxes(rows);
  const fmt = looksLikeMoney(prompt + " " + valueCol) ? formatINR : formatNum;
  return (
    <div style={{ padding: "12px 16px 16px" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
        {rows.map((r, i) => (
          <div key={i} style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "8px 0",
            borderBottom: i < rows.length - 1 ? `1px solid ${T.border.subtle}` : "none",
          }}>
            <span style={{ width: 22, fontSize: 11, fontWeight: 700, color: T.text.muted, fontVariantNumeric: "tabular-nums" }}>{i + 1}</span>
            <span style={{ flex: 1, fontSize: 13, color: T.text.primary, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {String(r[labelCol] ?? "—")}
            </span>
            <span style={{ fontSize: 13, fontWeight: 700, color: T.text.primary, fontVariantNumeric: "tabular-nums" }}>
              {fmt(r[valueCol])}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TableView({ response }) {
  const rows = (response?.results || []).slice(0, 50);
  const cols = Object.keys(rows[0] || {});
  if (rows.length === 0 || cols.length === 0) return <AnswerView response={response} />;
  return (
    <div style={{ padding: 0, maxHeight: 320, overflow: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr style={{ background: T.surface.card, position: "sticky", top: 0 }}>
            {cols.map(c => (
              <th key={c} style={{
                textAlign: "left", padding: "10px 14px",
                fontSize: 10, fontWeight: 800, letterSpacing: "0.06em",
                textTransform: "uppercase", color: T.text.muted,
                borderBottom: `1px solid ${T.border.subtle}`,
              }}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ borderBottom: `1px solid ${T.border.subtle}` }}>
              {cols.map(c => {
                const v = r[c];
                const isNum = typeof v === "number" ||
                  (typeof v === "string" && v !== "" && Number.isFinite(Number(v.replace(/,/g, ""))));
                return (
                  <td key={c} style={{
                    padding: "8px 14px", color: T.text.primary,
                    fontVariantNumeric: isNum ? "tabular-nums" : "normal",
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 220,
                  }}>
                    {v == null || v === "" ? "—" : isNum ? formatNum(v) : String(v)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Render Claude's markdown-ish answer as React nodes. No HTML injection —
 * we tokenize line by line and turn inline **bold** / *italic* / `code` into
 * safe React fragments; headings (`#`/`##`/`###`) become weighted spans.
 */
function renderInline(text) {
  if (!text) return null;
  const pattern = /\*\*(.+?)\*\*|`(.+?)`|\*(.+?)\*/g;
  const out = [];
  let last = 0, k = 0;
  let m = pattern.exec(text);
  while (m !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[1] !== undefined)      out.push(<strong key={`b${k++}`}>{m[1]}</strong>);
    else if (m[2] !== undefined) out.push(<code key={`c${k++}`} style={{ background: "rgba(252,128,25,0.10)", padding: "1px 4px", borderRadius: 3, color: "#FC8019", fontSize: 12 }}>{m[2]}</code>);
    else if (m[3] !== undefined) out.push(<em key={`i${k++}`}>{m[3]}</em>);
    last = m.index + m[0].length;
    m = pattern.exec(text);
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function AnswerBody({ text }) {
  const lines = String(text || "").split("\n");
  return (
    <div style={{ fontSize: 14, color: T.text.primary, lineHeight: 1.6 }}>
      {lines.map((line, i) => {
        const h3 = /^### (.+)/.exec(line);
        const h2 = /^## (.+)/.exec(line);
        const h1 = /^# (.+)/.exec(line);
        const li = /^- (.+)/.exec(line);
        if (h1) return <div key={i} style={{ fontSize: 17, fontWeight: 800, margin: "12px 0 6px", color: T.text.primary }}>{renderInline(h1[1])}</div>;
        if (h2) return <div key={i} style={{ fontSize: 15, fontWeight: 800, margin: "10px 0 6px", color: T.text.primary }}>{renderInline(h2[1])}</div>;
        if (h3) return <div key={i} style={{ fontSize: 13, fontWeight: 800, margin: "8px 0 4px",  color: T.text.primary }}>{renderInline(h3[1])}</div>;
        if (li) return <div key={i} style={{ paddingLeft: 14 }}>• {renderInline(li[1])}</div>;
        if (line.trim() === "") return <div key={i} style={{ height: 6 }} />;
        return <div key={i}>{renderInline(line)}</div>;
      })}
    </div>
  );
}

function AnswerView({ response }) {
  return (
    <div style={{ padding: "20px 22px" }}>
      <AnswerBody text={response?.answer || "No answer returned."} />
      {response?.rowCount != null && response.rowCount > 0 && (
        <div style={{ fontSize: 11, color: T.text.muted, marginTop: 10 }}>
          {response.rowCount} row{response.rowCount === 1 ? "" : "s"} returned.
        </div>
      )}
    </div>
  );
}

function CardBody({ card }) {
  if (card.loading) {
    return <div style={{ padding: "44px 22px", textAlign: "center", color: T.text.muted, fontSize: 13 }}>Asking the AI…</div>;
  }
  if (card.error) {
    return (
      <div style={{ padding: "20px 22px" }}>
        <div style={{
          padding: "12px 14px", borderRadius: 10,
          background: T.status.badBg, border: `1px solid ${T.status.bad}33`,
          color: T.status.bad, fontSize: 12, fontWeight: 600,
        }}>{card.error}</div>
      </div>
    );
  }
  if (!card.response) return null;
  // Decide effective shape:
  //  • "auto" → run autoShape detection
  //  • explicit type but no longer valid for this response shape → fall back
  //    to autoShape so we never render a misleading chart (e.g. Pie of one
  //    number, Line of category labels).
  const types = availableChartTypes(card.response);
  const requested = card.chartType || "auto";
  const effective = (requested === "auto" || !types.includes(requested))
    ? autoShape(card.response)
    : requested;
  switch (effective) {
    case "number":      return <NumberView      response={card.response} prompt={card.prompt} />;
    case "line":        return <LineView        response={card.response} prompt={card.prompt} />;
    case "bar":         return <BarView         response={card.response} prompt={card.prompt} />;
    case "pie":         return <PieView         response={card.response} prompt={card.prompt} />;
    case "leaderboard": return <LeaderboardView response={card.response} prompt={card.prompt} />;
    case "table":       return <TableView       response={card.response} />;
    default:            return <AnswerView      response={card.response} />;
  }
}

/* ───────────────────────── Card chrome ───────────────────────── */

function ChartTypeMenu({ current, onPick, types }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  // If the user previously chose a type that's no longer valid for this
  // data shape, the dropdown surfaces it as "Auto" so the card always
  // renders something sensible.
  const visibleCurrent = types.includes(current) ? current : "auto";
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button type="button" onClick={() => setOpen(v => !v)} title="Change chart"
        style={{
          height: 28, padding: "0 10px", borderRadius: 8,
          background: T.surface.canvas, border: `1px solid ${T.border.subtle}`,
          color: T.text.secondary, cursor: "pointer",
          fontSize: 11, fontWeight: 700, letterSpacing: "0.04em",
        }}>
        {CHART_LABEL[visibleCurrent] || "Auto"} ▾
      </button>
      {open && (
        <div role="menu" style={{
          position: "absolute", top: "calc(100% + 6px)", left: 0,
          minWidth: 160, background: T.surface.card,
          border: `1px solid ${T.border.subtle}`, borderRadius: 10,
          boxShadow: "0 12px 28px rgba(31,42,63,0.18)",
          padding: 4, zIndex: 50,
          fontFamily: "inherit",
        }}>
          {types.map(t => (
            <button key={t} type="button" onClick={() => { onPick(t); setOpen(false); }}
              style={{
                display: "block", width: "100%", textAlign: "left",
                padding: "8px 14px",
                background: t === visibleCurrent ? T.brand.soft : "transparent",
                border: 0, borderRadius: 6,
                fontSize: 13, fontWeight: 600,
                color: t === visibleCurrent ? T.brand.deep : T.text.primary,
                cursor: "pointer", fontFamily: "inherit",
                whiteSpace: "nowrap",
              }}
              onMouseEnter={(e) => { if (t !== visibleCurrent) e.currentTarget.style.background = T.surface.canvas; }}
              onMouseLeave={(e) => { if (t !== visibleCurrent) e.currentTarget.style.background = "transparent"; }}
            >{CHART_LABEL[t]}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function CardHeader({ card, onRefresh, onRemove, onChartTypeChange }) {
  // Compute available chart types for THIS card's data so the dropdown
  // only offers options that produce a meaningful render.
  const types = card.response
    ? availableChartTypes(card.response)
    : ["auto"];
  const showChartPicker = card.response && types.length > 2;
  return (
    <div style={{
      padding: "14px 16px",
      borderBottom: `1px solid ${T.border.subtle}`,
      background: T.surface.card,
    }}>
      <div style={{
        fontSize: 15, fontWeight: 700, color: T.text.primary,
        lineHeight: 1.4, wordBreak: "break-word",
        letterSpacing: "-0.005em",
      }}>
        {card.prompt}
      </div>
      <div className="fc-card-actions" style={{
        display: "flex", alignItems: "center", gap: 8,
        marginTop: 10, flexWrap: "wrap",
      }}>
        {showChartPicker && (
          <ChartTypeMenu current={card.chartType || "auto"} onPick={onChartTypeChange} types={types} />
        )}
        <div style={{ flex: 1 }} />
        <button type="button" onClick={onRefresh} title="Refresh this card"
          disabled={card.loading}
          style={{
            display: "flex", alignItems: "center", gap: 5,
            height: 28, padding: "0 10px", borderRadius: 8,
            background: T.surface.canvas, border: `1px solid ${T.border.subtle}`,
            color: T.text.secondary, cursor: card.loading ? "wait" : "pointer",
            fontSize: 11, fontWeight: 700, fontFamily: "inherit",
          }}>↻ <span>Refresh</span></button>
        <button type="button" onClick={onRemove} title="Remove from dashboard"
          style={{
            width: 28, height: 28, borderRadius: 8,
            background: T.surface.canvas, border: `1px solid ${T.border.subtle}`,
            color: T.text.muted, cursor: "pointer",
            fontSize: 16, fontFamily: "inherit",
          }}>×</button>
      </div>
    </div>
  );
}

function timeAgo(ts) {
  if (!ts) return "—";
  const sec = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (sec < 60)  return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

function Card({ card, onRefresh, onRemove, onChartTypeChange }) {
  return (
    <div style={{
      background: T.surface.card,
      border: `1px solid ${T.border.subtle}`,
      borderRadius: 14,
      // overflow:hidden was clipping the chart-type dropdown menu (which is
      // absolutely-positioned and extends past the card edge). Switch to
      // `visible` — inner CardHeader/CardBody have their own padding so
      // nothing visually bleeds past the rounded corners.
      overflow: "visible",
      boxShadow: "0 4px 14px rgba(31,42,63,0.04)",
      display: "flex", flexDirection: "column",
    }}>
      <CardHeader card={card} onRefresh={onRefresh} onRemove={onRemove} onChartTypeChange={onChartTypeChange} />
      <div style={{ flex: 1, overflow: "hidden", borderRadius: "0 0 14px 14px" }}>
        <CardBody card={card} />
      </div>
      {card.response?.rowCount != null && (
        <div style={{
          padding: "8px 16px",
          fontSize: 10, color: T.text.muted,
          borderTop: `1px solid ${T.border.subtle}`,
          background: T.surface.canvas,
          display: "flex", justifyContent: "space-between",
        }}>
          <span>Asked {timeAgo(card.addedAt)}</span>
          {card.response.rowCount > 0 && <span>{card.response.rowCount} rows</span>}
        </div>
      )}
    </div>
  );
}

/* ───────────────────────── Export helpers ───────────────────────── */

function csvEscape(v) {
  if (v == null) return "";
  const s = String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function exportCSV(dashboard) {
  const lines = [];
  lines.push(`Fancall Dashboard — ${dashboard.name}`);
  lines.push(`Exported,${new Date().toLocaleString("en-IN")}`);
  lines.push(`Cards,${dashboard.cards.length}`);
  lines.push("");
  for (const card of dashboard.cards) {
    lines.push("--- Card ---");
    lines.push(`Prompt,${csvEscape(card.prompt)}`);
    lines.push(`Asked at,${new Date(card.addedAt || Date.now()).toLocaleString("en-IN")}`);
    if (card.response?.answer) lines.push(`Summary,${csvEscape(card.response.answer.replace(/\n+/g, " "))}`);
    const rows = card.response?.results;
    if (Array.isArray(rows) && rows.length > 0) {
      const cols = Object.keys(rows[0]);
      lines.push(cols.join(","));
      for (const r of rows) lines.push(cols.map(c => csvEscape(r[c])).join(","));
    }
    lines.push("");
  }
  // UTF-8 BOM so Excel opens it cleanly with proper Indian characters.
  const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${dashboard.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a); a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
}

function exportPDF() {
  // Use the browser's native print → "Save as PDF". Print-friendly CSS is
  // injected via the `fc-print` class on the page body; the surrounding
  // sidebar/topbar are hidden so the PDF only contains the dashboard.
  document.body.classList.add("fc-printing");
  setTimeout(() => {
    window.print();
    document.body.classList.remove("fc-printing");
  }, 50);
}

/* ───────────────────────── Page ───────────────────────── */

export function CustomDashboardPage({ authToken }) {
  const [state, setState] = useState(() => loadState());
  const [prompt, setPrompt] = useState("");
  const [adding, setAdding] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameDraft, setRenameDraft] = useState("");

  useEffect(() => { saveState(state); }, [state]);

  const active = useMemo(
    () => state.dashboards.find(d => d.id === state.activeId) || state.dashboards[0],
    [state.dashboards, state.activeId],
  );

  const updateActive = useCallback((updater) => {
    setState(prev => {
      const dashboards = prev.dashboards.map(d => {
        if (d.id !== prev.activeId) return d;
        return { ...updater(d), updatedAt: Date.now() };
      });
      return { ...prev, dashboards };
    });
  }, []);

  const askAndStore = useCallback(async (q) => {
    if (!authToken) throw new Error("Not signed in.");
    return await authApi.askAI(authToken, q);
  }, [authToken]);

  /* ── Card ops ── */
  const handleAdd = async () => {
    const q = prompt.trim();
    if (!q || adding || !active) return;
    setAdding(true);
    const id = newCardId();
    const placeholder = { id, prompt: q, response: null, loading: true, error: null, addedAt: Date.now(), chartType: "auto" };
    updateActive(d => ({ ...d, cards: [placeholder, ...d.cards] }));
    setPrompt("");
    try {
      const response = await askAndStore(q);
      updateActive(d => ({ ...d, cards: d.cards.map(c => c.id === id ? { ...c, response, loading: false } : c) }));
    } catch (err) {
      updateActive(d => ({ ...d, cards: d.cards.map(c => c.id === id ? { ...c, error: err.message || "Failed to fetch", loading: false } : c) }));
    } finally {
      setAdding(false);
    }
  };

  const refreshCard = useCallback(async (cardId) => {
    if (!active) return;
    const card = active.cards.find(c => c.id === cardId);
    if (!card) return;
    updateActive(d => ({ ...d, cards: d.cards.map(c => c.id === cardId ? { ...c, loading: true, error: null } : c) }));
    try {
      const response = await askAndStore(card.prompt);
      updateActive(d => ({ ...d, cards: d.cards.map(c => c.id === cardId ? { ...c, response, loading: false, addedAt: Date.now() } : c) }));
    } catch (err) {
      updateActive(d => ({ ...d, cards: d.cards.map(c => c.id === cardId ? { ...c, error: err.message || "Failed to fetch", loading: false } : c) }));
    }
  }, [active, askAndStore, updateActive]);

  const removeCard = (cardId) => {
    updateActive(d => ({ ...d, cards: d.cards.filter(c => c.id !== cardId) }));
  };

  // Auto-refresh stale cards on first mount of the active dashboard. A card
  // is "stale" if its stored response has rowCount > 0 but results is null —
  // that happens when the card was originally asked while the backend was in
  // production/strict-hide mode (results stripped). The user can no longer
  // see charts for those rows. Quietly re-ask the AI so live data is restored.
  // Only fires once per (dashboard,card) per page load. Avoids hammering on
  // re-renders.
  const autoRefreshedRef = useRef(new Set());
  useEffect(() => {
    if (!active) return;
    const stale = active.cards.filter(c => {
      if (autoRefreshedRef.current.has(c.id)) return false;
      const r = c.response;
      if (!r) return false;
      if (c.loading) return false;
      // Has answer or rowCount > 0 but results is null/empty → stripped earlier.
      const hasAnswer = !!r.answer;
      const claimsRows = (r.rowCount || 0) > 0;
      const resultsMissing = !Array.isArray(r.results) || r.results.length === 0;
      return (hasAnswer || claimsRows) && resultsMissing;
    });
    if (stale.length === 0) return;
    stale.forEach(c => autoRefreshedRef.current.add(c.id));
    // Refresh sequentially to respect the AI rate limiter (5/min burst).
    (async () => {
      for (const c of stale) {
        // eslint-disable-next-line no-await-in-loop
        await refreshCard(c.id);
      }
    })();
  }, [active, refreshCard]);

  const setCardChartType = (cardId, chartType) => {
    updateActive(d => ({ ...d, cards: d.cards.map(c => c.id === cardId ? { ...c, chartType } : c) }));
  };

  const refreshAll = async () => {
    if (!active) return;
    for (const c of active.cards) {
      // eslint-disable-next-line no-await-in-loop
      await refreshCard(c.id);
    }
  };

  const clearAll = () => {
    if (!active || active.cards.length === 0) return;
    if (window.confirm(`Remove all ${active.cards.length} cards from "${active.name}"?`)) {
      updateActive(d => ({ ...d, cards: [] }));
    }
  };

  /* ── Dashboard ops ── */
  const newDashboard = () => {
    const name = window.prompt("Name your dashboard:", `Dashboard ${state.dashboards.length + 1}`);
    if (!name || !name.trim()) return;
    const id = newDashboardId();
    setState(prev => ({
      activeId: id,
      dashboards: [...prev.dashboards, { id, name: name.trim(), cards: [], createdAt: Date.now(), updatedAt: Date.now() }],
    }));
  };

  const switchTo = (id) => setState(prev => ({ ...prev, activeId: id }));

  const startRename = (id, currentName) => {
    setRenamingId(id);
    setRenameDraft(currentName);
  };
  const commitRename = () => {
    const name = renameDraft.trim();
    if (!name || !renamingId) { setRenamingId(null); return; }
    setState(prev => ({
      ...prev,
      dashboards: prev.dashboards.map(d => d.id === renamingId ? { ...d, name, updatedAt: Date.now() } : d),
    }));
    setRenamingId(null);
  };

  const deleteDashboard = (id) => {
    const target = state.dashboards.find(d => d.id === id);
    if (!target) return;
    if (state.dashboards.length === 1) {
      alert("You must have at least one dashboard. Rename it instead, or use Clear all to empty its cards.");
      return;
    }
    if (!window.confirm(`Delete "${target.name}" and all ${target.cards.length} cards on it?`)) return;
    setState(prev => {
      const dashboards = prev.dashboards.filter(d => d.id !== id);
      const activeId = prev.activeId === id ? dashboards[0].id : prev.activeId;
      return { dashboards, activeId };
    });
  };

  if (!authToken) {
    return <EmptyError title="Sign in to build your own dashboard" error="No session" onRetry={null} />;
  }
  if (!active) return null;

  /* ── Render ── */

  const pinnedLower = new Set(active.cards.map(c => c.prompt.trim().toLowerCase()));
  const remainingSuggestions = SUGGESTIONS.filter(s => !pinnedLower.has(s.toLowerCase()));

  return (
    <>
      {/* Print-only CSS — hides everything except `.fc-print-area` while
          window.print() is active so the saved PDF contains only the dashboard. */}
      <style>{`
        @media print {
          body.fc-printing aside,
          body.fc-printing header,
          body.fc-printing .fc-no-print { display: none !important; }
          body.fc-printing main { padding: 0 !important; }
          body.fc-printing .fc-print-area { padding: 12mm !important; max-width: none !important; }
          body.fc-printing .fc-card-actions { display: none !important; }
          body.fc-printing { background: #ffffff !important; }
        }
      `}</style>

      <div className="fc-print-area">

      {/* ───── Dashboard tab strip ───── */}
      <div className="fc-no-print" style={{
        display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
        marginBottom: 18, paddingBottom: 14,
        borderBottom: `1px solid ${T.border.subtle}`,
      }}>
        {state.dashboards.map(d => {
          const isActive = d.id === state.activeId;
          const isRenaming = renamingId === d.id;
          if (isRenaming) {
            return (
              <input key={d.id} autoFocus
                value={renameDraft}
                onChange={(e) => setRenameDraft(e.target.value)}
                onBlur={commitRename}
                onKeyDown={(e) => { if (e.key === "Enter") commitRename(); if (e.key === "Escape") setRenamingId(null); }}
                style={{
                  padding: "8px 14px", borderRadius: 999,
                  border: `1px solid ${T.brand.base}`, fontSize: 13, fontWeight: 700,
                  background: T.surface.card, color: T.text.primary,
                  fontFamily: "inherit", outline: "none",
                  boxShadow: `0 0 0 3px ${T.brand.soft}`,
                  minWidth: 160,
                }}
              />
            );
          }
          return (
            <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 0 }}>
              <button type="button" onClick={() => switchTo(d.id)}
                onDoubleClick={() => startRename(d.id, d.name)}
                style={{
                  padding: "8px 14px",
                  borderTopLeftRadius: 999, borderBottomLeftRadius: 999,
                  borderTopRightRadius: 0, borderBottomRightRadius: 0,
                  border: `1px solid ${isActive ? T.brand.base : T.border.subtle}`,
                  borderRight: "none",
                  background: isActive
                    ? `linear-gradient(135deg, ${T.brand.base} 0%, ${T.brand.deep} 100%)`
                    : T.surface.card,
                  color: isActive ? "#FFFFFF" : T.text.primary,
                  fontSize: 13, fontWeight: 700, letterSpacing: "0.01em",
                  cursor: "pointer", fontFamily: "inherit",
                }}>
                {d.name}
                <span style={{
                  marginLeft: 8, fontSize: 10, fontWeight: 600,
                  color: isActive ? "rgba(255,255,255,0.85)" : T.text.muted,
                }}>{d.cards.length}</span>
              </button>
              <button type="button" onClick={() => isActive ? startRename(d.id, d.name) : null}
                title={isActive ? "Rename" : "Switch first to edit"}
                disabled={!isActive}
                style={{
                  width: 32, height: 34,
                  border: `1px solid ${isActive ? T.brand.base : T.border.subtle}`,
                  borderLeft: "none", borderRight: "none",
                  background: isActive
                    ? `linear-gradient(135deg, ${T.brand.base} 0%, ${T.brand.deep} 100%)`
                    : T.surface.card,
                  color: isActive ? "#FFFFFF" : T.text.muted,
                  cursor: isActive ? "pointer" : "default",
                  fontSize: 12, fontFamily: "inherit",
                }}>✎</button>
              <button type="button" onClick={() => deleteDashboard(d.id)} title="Delete"
                style={{
                  width: 32, height: 34,
                  borderTopRightRadius: 999, borderBottomRightRadius: 999,
                  borderTopLeftRadius: 0, borderBottomLeftRadius: 0,
                  border: `1px solid ${isActive ? T.brand.base : T.border.subtle}`,
                  background: isActive
                    ? `linear-gradient(135deg, ${T.brand.base} 0%, ${T.brand.deep} 100%)`
                    : T.surface.card,
                  color: isActive ? "rgba(255,255,255,0.95)" : T.text.muted,
                  cursor: "pointer",
                  fontSize: 14, fontFamily: "inherit",
                }}>×</button>
            </div>
          );
        })}
        <button type="button" onClick={newDashboard}
          style={{
            padding: "8px 14px", borderRadius: 999,
            border: `1px dashed ${T.border.default}`, background: "transparent",
            color: T.brand.base, fontSize: 13, fontWeight: 700,
            cursor: "pointer", fontFamily: "inherit",
          }}>+ New dashboard</button>
      </div>

      {/* ───── Header ───── */}
      <div style={{ marginBottom: 16, padding: "0 4px", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: T.text.primary, letterSpacing: "-0.01em" }}>{active.name}</div>
          <div style={{ fontSize: 12, color: T.text.muted, marginTop: 4 }}>
            {active.cards.length} card{active.cards.length === 1 ? "" : "s"} ·
            updated {timeAgo(active.updatedAt)} ·
            ask in plain English, pick a chart, export to CSV / PDF.
          </div>
        </div>
        <div className="fc-no-print" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {active.cards.length > 0 && (
            <>
              <button type="button" onClick={refreshAll} title="Refresh every card"
                style={btnSecondary()}>↻ Refresh all</button>
              <button type="button" onClick={() => exportCSV(active)} title="Download as CSV (opens in Excel)"
                style={btnSecondary()}>⬇ CSV</button>
              <button type="button" onClick={exportPDF} title="Print → Save as PDF"
                style={btnSecondary()}>⬇ PDF</button>
              <button type="button" onClick={clearAll} title="Empty this dashboard"
                style={btnSecondary({ color: T.status.bad })}>Clear all</button>
            </>
          )}
        </div>
      </div>

      {/* ───── Editor ───── */}
      <div className="fc-no-print" style={{
        background: T.surface.card,
        border: `1px solid ${T.border.subtle}`,
        borderRadius: 14,
        padding: 18,
        marginBottom: 16,
        boxShadow: "0 4px 14px rgba(31,42,63,0.04)",
      }}>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleAdd(); } }}
          placeholder="e.g. Top 5 creators by earnings this month"
          rows={3}
          style={{
            width: "100%", padding: "12px 14px",
            border: `1px solid ${T.border.default}`, borderRadius: 10,
            fontSize: 14, fontFamily: "inherit", color: T.text.primary,
            background: T.surface.canvas, resize: "vertical", minHeight: 70,
            outline: "none", boxSizing: "border-box",
            transition: "border-color 160ms ease, box-shadow 160ms ease",
          }}
          onFocus={(e) => { e.target.style.borderColor = T.brand.base; e.target.style.boxShadow = `0 0 0 3px ${T.brand.soft}`; }}
          onBlur={(e) => { e.target.style.borderColor = T.border.default; e.target.style.boxShadow = "none"; }}
        />
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
          <button type="button" onClick={handleAdd} disabled={adding || !prompt.trim()}
            style={{
              padding: "10px 18px", borderRadius: 10, border: "none",
              background: adding || !prompt.trim()
                ? T.border.subtle
                : `linear-gradient(135deg, ${T.brand.base} 0%, ${T.brand.deep} 100%)`,
              color: adding || !prompt.trim() ? T.text.muted : "#FFFFFF",
              fontSize: 13, fontWeight: 700,
              cursor: adding || !prompt.trim() ? "not-allowed" : "pointer",
              boxShadow: adding || !prompt.trim() ? "none" : `0 6px 16px ${T.brand.soft}`,
            }}>
            {adding ? "Adding…" : `+ Add to ${active.name}`}
          </button>
          <span style={{ fontSize: 11, color: T.text.muted }}>Ctrl/⌘ + Enter to add</span>
        </div>
        {remainingSuggestions.length > 0 && (
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${T.border.subtle}` }}>
            <div style={{
              display: "flex", justifyContent: "space-between",
              alignItems: "center", marginBottom: 8,
            }}>
              <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.10em", textTransform: "uppercase", color: T.text.muted }}>
                Quick starts
              </span>
              <span style={{ fontSize: 10, color: T.text.muted, fontWeight: 600 }}>
                {remainingSuggestions.length} of {SUGGESTIONS.length} available
              </span>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {remainingSuggestions.map(s => (
                <button key={s} type="button" onClick={() => setPrompt(s)}
                  style={{
                    padding: "6px 12px", borderRadius: 999,
                    background: T.surface.canvas, border: `1px solid ${T.border.subtle}`,
                    color: T.text.secondary, fontSize: 12, fontWeight: 500,
                    cursor: "pointer", fontFamily: "inherit",
                  }}>{s}</button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ───── Cards ───── */}
      {active.cards.length === 0 ? (
        <div style={{
          background: T.surface.card,
          border: `1px dashed ${T.border.default}`,
          borderRadius: 14,
          padding: "40px 24px",
          textAlign: "center",
          color: T.text.secondary,
        }}>
          <div style={{
            fontSize: 56, lineHeight: 1, marginBottom: 12,
            background: `linear-gradient(135deg, ${T.brand.base} 0%, ${T.brand.deep} 100%)`,
            WebkitBackgroundClip: "text", backgroundClip: "text",
            WebkitTextFillColor: "transparent", color: "transparent",
          }}>+</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.text.primary, marginBottom: 4 }}>
            "{active.name}" is empty
          </div>
          <div style={{ fontSize: 13, color: T.text.muted, maxWidth: 420, margin: "0 auto" }}>
            Type a question above and click &ldquo;Add to {active.name}&rdquo;. Each card supports number / line / bar / pie / list / table views.
          </div>
        </div>
      ) : (
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(360px, 100%), 1fr))",
          gap: 14,
        }}>
          {active.cards.map(c => (
            <Card key={c.id} card={c}
              onRefresh={() => refreshCard(c.id)}
              onRemove={() => removeCard(c.id)}
              onChartTypeChange={(t) => setCardChartType(c.id, t)}
            />
          ))}
        </div>
      )}

      </div>
    </>
  );
}

function btnSecondary(extra = {}) {
  return {
    padding: "8px 14px", borderRadius: 10,
    border: `1px solid ${T.border.subtle}`,
    background: T.surface.card, color: T.text.primary,
    fontSize: 12, fontWeight: 700,
    cursor: "pointer", fontFamily: "inherit",
    ...extra,
  };
}
